import React, { useState, useEffect } from 'react';
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  doc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { useTheme } from '../contexts/ThemeContext';
import {
  MessageCircle,
  Clock,
  CheckCircle,
  XCircle,
  Search,
  ChevronDown,
  User,
  Mail,
  Phone,
  Tag,
  Calendar,
  X,
} from 'lucide-react';
import toast from 'react-hot-toast';

const STATUS_OPTIONS = ['all', 'new', 'in_progress', 'resolved', 'closed'];

const statusMeta = {
  new: { label: 'New', color: '#3B82F6', bg: 'rgba(59,130,246,0.12)', Icon: Clock },
  in_progress: { label: 'In Progress', color: '#F59E0B', bg: 'rgba(245,158,11,0.12)', Icon: MessageCircle },
  resolved: { label: 'Resolved', color: '#22C55E', bg: 'rgba(34,197,94,0.12)', Icon: CheckCircle },
  closed: { label: 'Closed', color: '#6B7280', bg: 'rgba(107,114,128,0.12)', Icon: XCircle },
};

const SupportMessages = () => {
  const { colors } = useTheme();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [adminNote, setAdminNote] = useState('');
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    const q = query(collection(db, 'supportMessages'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, (snap) => {
      setMessages(
        snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
          createdAt: d.data().createdAt?.toDate(),
          resolvedAt: d.data().resolvedAt?.toDate(),
        }))
      );
      setLoading(false);
    });
    return unsub;
  }, []);

  const filtered = messages.filter((m) => {
    const matchStatus = statusFilter === 'all' || m.status === statusFilter;
    const term = search.toLowerCase();
    const matchSearch =
      !term ||
      m.userName?.toLowerCase().includes(term) ||
      m.subject?.toLowerCase().includes(term) ||
      m.message?.toLowerCase().includes(term) ||
      m.userPhone?.includes(term) ||
      m.userEmail?.toLowerCase().includes(term);
    return matchStatus && matchSearch;
  });

  const openDetail = (msg) => {
    setSelected(msg);
    setAdminNote(msg.adminNote || '');
  };

  const updateStatus = async (newStatus) => {
    if (!selected) return;
    setUpdating(true);
    try {
      await updateDoc(doc(db, 'supportMessages', selected.id), {
        status: newStatus,
        adminNote: adminNote || null,
        resolvedAt: newStatus === 'resolved' ? serverTimestamp() : null,
        updatedAt: serverTimestamp(),
      });
      setSelected((prev) => ({ ...prev, status: newStatus, adminNote }));
      setMessages((prev) =>
        prev.map((m) =>
          m.id === selected.id ? { ...m, status: newStatus, adminNote } : m
        )
      );
      toast.success(`Status updated to "${newStatus}"`);
    } catch (e) {
      toast.error('Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const saveNote = async () => {
    if (!selected) return;
    setUpdating(true);
    try {
      await updateDoc(doc(db, 'supportMessages', selected.id), {
        adminNote: adminNote || null,
        updatedAt: serverTimestamp(),
      });
      setSelected((prev) => ({ ...prev, adminNote }));
      toast.success('Note saved');
    } catch (e) {
      toast.error('Failed to save note');
    } finally {
      setUpdating(false);
    }
  };

  const fmt = (date) =>
    date
      ? date.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      : '—';

  const newCount = messages.filter((m) => m.status === 'new').length;

  return (
    <div className="p-6 max-w-7xl mx-auto" style={{ color: colors.text }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: colors.text }}>
            Support Messages
          </h1>
          <p className="text-sm mt-0.5" style={{ color: colors.textMuted }}>
            {newCount > 0 ? `${newCount} new message${newCount > 1 ? 's' : ''} awaiting response` : 'All messages reviewed'}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        {/* Search */}
        <div
          className="flex items-center gap-2 px-3 py-2 rounded-xl flex-1 min-w-[200px]"
          style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}
        >
          <Search className="w-4 h-4" style={{ color: colors.textMuted }} />
          <input
            className="bg-transparent outline-none text-sm w-full"
            style={{ color: colors.text }}
            placeholder="Search by name, subject, phone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* Status filter */}
        <div className="relative">
          <select
            className="appearance-none pl-3 pr-8 py-2 rounded-xl text-sm font-medium cursor-pointer"
            style={{
              backgroundColor: colors.surface,
              color: colors.text,
              border: `1px solid ${colors.border}`,
            }}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s === 'all' ? 'All Statuses' : statusMeta[s]?.label ?? s}
              </option>
            ))}
          </select>
          <ChevronDown
            className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none"
            style={{ color: colors.textMuted }}
          />
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {Object.entries(statusMeta).map(([key, meta]) => {
          const count = messages.filter((m) => m.status === key).length;
          const { Icon } = meta;
          return (
            <div
              key={key}
              className="flex items-center gap-3 p-4 rounded-xl cursor-pointer"
              style={{
                backgroundColor: statusFilter === key ? meta.bg : colors.surface,
                border: `1px solid ${statusFilter === key ? meta.color + '40' : colors.border}`,
              }}
              onClick={() => setStatusFilter(statusFilter === key ? 'all' : key)}
            >
              <div
                className="w-9 h-9 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: meta.bg }}
              >
                <Icon className="w-4 h-4" style={{ color: meta.color }} />
              </div>
              <div>
                <p className="text-xl font-bold" style={{ color: meta.color }}>{count}</p>
                <p className="text-xs" style={{ color: colors.textMuted }}>{meta.label}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div
            className="w-10 h-10 rounded-full border-2 border-t-transparent animate-spin"
            style={{ borderColor: colors.primary }}
          />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20" style={{ color: colors.textMuted }}>
          <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p>No messages found</p>
        </div>
      ) : (
        <div
          className="rounded-2xl overflow-hidden"
          style={{ border: `1px solid ${colors.border}` }}
        >
          <table className="w-full text-sm">
            <thead>
              <tr style={{ backgroundColor: colors.surface, borderBottom: `1px solid ${colors.border}` }}>
                {['User', 'Subject', 'Status', 'Date', ''].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-3 text-left font-semibold text-xs uppercase tracking-wider"
                    style={{ color: colors.textMuted }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((msg, i) => {
                const meta = statusMeta[msg.status] || statusMeta.new;
                const { Icon } = meta;
                return (
                  <tr
                    key={msg.id}
                    className="cursor-pointer transition-colors"
                    style={{
                      backgroundColor:
                        i % 2 === 0 ? colors.background : colors.surface,
                      borderBottom: `1px solid ${colors.border}`,
                    }}
                    onClick={() => openDetail(msg)}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = colors.primaryGhost)}
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor =
                        i % 2 === 0 ? colors.background : colors.surface)
                    }
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium" style={{ color: colors.text }}>
                        {msg.userName || '—'}
                      </div>
                      <div className="text-xs mt-0.5" style={{ color: colors.textMuted }}>
                        {msg.userPhone || msg.userEmail || '—'}
                      </div>
                    </td>
                    <td className="px-4 py-3" style={{ color: colors.text }}>
                      <div className="font-medium truncate max-w-[200px]">{msg.subject || '—'}</div>
                      <div
                        className="text-xs mt-0.5 truncate max-w-[200px]"
                        style={{ color: colors.textMuted }}
                      >
                        {msg.message}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold"
                        style={{ backgroundColor: meta.bg, color: meta.color }}
                      >
                        <Icon className="w-3 h-3" />
                        {meta.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs" style={{ color: colors.textMuted }}>
                      {fmt(msg.createdAt)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        className="text-xs font-medium px-3 py-1.5 rounded-lg transition-colors"
                        style={{
                          backgroundColor: colors.primaryGhost,
                          color: colors.primary,
                        }}
                        onClick={(e) => { e.stopPropagation(); openDetail(msg); }}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail panel */}
      {selected && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-end"
          style={{ backgroundColor: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(4px)' }}
          onClick={() => setSelected(null)}
        >
          <div
            className="h-full w-full max-w-md overflow-y-auto flex flex-col"
            style={{ backgroundColor: colors.background }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Panel header */}
            <div
              className="flex items-center justify-between px-6 py-4 sticky top-0"
              style={{
                backgroundColor: colors.background,
                borderBottom: `1px solid ${colors.border}`,
              }}
            >
              <h2 className="font-bold text-lg" style={{ color: colors.text }}>
                Message Detail
              </h2>
              <button
                onClick={() => setSelected(null)}
                className="p-1.5 rounded-lg transition-colors"
                style={{ color: colors.textMuted }}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 py-4 space-y-5">
              {/* User info */}
              <div
                className="rounded-2xl p-4 space-y-3"
                style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}
              >
                <div className="flex items-center gap-2 text-sm">
                  <User className="w-4 h-4" style={{ color: colors.textMuted }} />
                  <span style={{ color: colors.text }}>{selected.userName || '—'}</span>
                </div>
                {selected.userPhone && (
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="w-4 h-4" style={{ color: colors.textMuted }} />
                    <span style={{ color: colors.text }}>{selected.userPhone}</span>
                  </div>
                )}
                {selected.userEmail && (
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="w-4 h-4" style={{ color: colors.textMuted }} />
                    <span style={{ color: colors.text }}>{selected.userEmail}</span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="w-4 h-4" style={{ color: colors.textMuted }} />
                  <span style={{ color: colors.textMuted }}>{fmt(selected.createdAt)}</span>
                </div>
              </div>

              {/* Subject */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Tag className="w-4 h-4" style={{ color: colors.textMuted }} />
                  <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: colors.textMuted }}>
                    Subject
                  </span>
                </div>
                <p className="font-semibold text-sm" style={{ color: colors.text }}>
                  {selected.subject || '—'}
                </p>
              </div>

              {/* Message */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <MessageCircle className="w-4 h-4" style={{ color: colors.textMuted }} />
                  <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: colors.textMuted }}>
                    Message
                  </span>
                </div>
                <p
                  className="text-sm leading-relaxed p-4 rounded-xl"
                  style={{
                    color: colors.text,
                    backgroundColor: colors.surface,
                    border: `1px solid ${colors.border}`,
                  }}
                >
                  {selected.message}
                </p>
              </div>

              {/* Status */}
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: colors.textMuted }}>
                  Update Status
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(statusMeta).map(([key, meta]) => {
                    const { Icon } = meta;
                    const isActive = selected.status === key;
                    return (
                      <button
                        key={key}
                        disabled={updating}
                        onClick={() => updateStatus(key)}
                        className="flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium transition-all"
                        style={{
                          backgroundColor: isActive ? meta.bg : colors.surface,
                          color: isActive ? meta.color : colors.textSecondary,
                          border: `1px solid ${isActive ? meta.color + '50' : colors.border}`,
                          opacity: updating ? 0.6 : 1,
                        }}
                      >
                        <Icon className="w-4 h-4" />
                        {meta.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Admin note */}
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: colors.textMuted }}>
                  Admin Note
                </p>
                <textarea
                  rows={4}
                  className="w-full rounded-xl p-3 text-sm outline-none resize-none"
                  style={{
                    backgroundColor: colors.surface,
                    color: colors.text,
                    border: `1px solid ${colors.border}`,
                  }}
                  placeholder="Add internal note..."
                  value={adminNote}
                  onChange={(e) => setAdminNote(e.target.value)}
                />
                <button
                  disabled={updating}
                  onClick={saveNote}
                  className="mt-2 w-full py-2.5 rounded-xl text-sm font-semibold transition-colors"
                  style={{
                    backgroundColor: colors.primary,
                    color: '#fff',
                    opacity: updating ? 0.7 : 1,
                  }}
                >
                  {updating ? 'Saving...' : 'Save Note'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportMessages;
